/**
 * Helper functions for creating payment intents in DevTool
 * Simplified version for testing purposes
 */

export interface PaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
}

export class PaymentIntentError extends Error {
  code: "NETWORK_ERROR" | "BACKEND_ERROR" | "VALIDATION_ERROR";
  details?: unknown;

  constructor(
    code: "NETWORK_ERROR" | "BACKEND_ERROR" | "VALIDATION_ERROR",
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = "PaymentIntentError";
  }
}

interface CreatePaymentIntentParams {
  amount: number; // in cents
  orgId: number;
}

/**
 * Create a test payment intent for DevTool testing
 * Throws PaymentIntentError on failure
 */
export const createPaymentIntent = async (
  params: CreatePaymentIntentParams,
): Promise<PaymentIntentResponse> => {
  try {
    // Validate amount
    if (params.amount < 50) {
      throw new PaymentIntentError(
        "VALIDATION_ERROR",
        "Amount must be at least $0.50",
      );
    }

    // Create test payment intent with Terminal-compatible params
    const response = await fetch("/api/stripe/create-payment-intent", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        amount: params.amount,
        // Mock user for testing
        user: {
          id: "test-user",
          fname: "Test",
          lname: "User",
        },
        orgName: "Test Org",
        creationSource: "kiosk-devtool",
        // Mock consumer for testing
        consumer: {
          id: "test-consumer",
        },
        collectedBy: "kiosk-devtool",
        // Required for Terminal payments
        paymentMethodType: "card_present",
        // Allow redisplay is required when saving payment methods with Terminal
        // Options: 'always', 'limited', 'unspecified'
        allowRedisplay: "always",
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new PaymentIntentError(
        "BACKEND_ERROR",
        error.error || "Failed to create payment intent",
        error,
      );
    }

    const data = await response.json();
    return {
      clientSecret: data.clientSecret,
      paymentIntentId: data.paymentIntentId,
      amount: params.amount,
    };
  } catch (error) {
    // Re-throw PaymentIntentError as-is
    if (error instanceof PaymentIntentError) {
      throw error;
    }

    // Wrap other errors
    throw new PaymentIntentError(
      "NETWORK_ERROR",
      error instanceof Error ? error.message : "Network error occurred",
      error,
    );
  }
};
